import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, FileText } from 'lucide-react';
import { FormModal, Field, FilePicker, inputCls, btnPrimary, btnGhost } from '../components/shared/FormKit';
import NowName from '../components/shared/NowName';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import AttachmentsList from '../components/workflow/AttachmentsList';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider } from '../components/workflow/fieldComments';
import ApprovalsCard from '../components/workflow/ApprovalsCard';
import ReviewerSelect, { useClearOffRosterPick } from '../components/workflow/ReviewerSelect';
import { EngagementName, EngagementNameList, AddedBadge, RemovedBadge, stripDupFyeLabel, fmtCoverageDefault } from '../components/workflow/CoverageDisplay';
import { Qc35ChecklistForm, Qc35ChecklistView } from '../components/workflow/Qc35Checklist';
import { qc35AnswerGaps, qc35AnswerLabels, expandQc35AnswerChanges, sameQc35Answers } from './qc35Questions';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { fetchJSON, API_BASE_URL } from '../utils/api';

// QC35 Form Amendment — detail page (docs/modules/workflow-qc35.md).

// 退件逐項意見用的欄位名。這條能改的是修正理由、更正截圖（files 由
// SHARED_CHANGE_LABELS 命名）、更正歸檔日與更正 checklist——QC35 從不動
// 簽核日。checklist 的逐題名字住在題庫裡，併進來逐題意見才叫得出題目。
const QC35A_CHANGE_LABELS = {
  reason: 'Reason for the Amendment',
  archivalDate: 'Corrected Archival Date',
  // 一整包答案的 diff 由 expandQc35AnswerChanges 攤成逐題列；這個標籤只在
  // 攤不開（形狀不對的 legacy 資料）時墊底。
  answers: 'File Archival Checklist',
  reviewerName: 'Reviewer',
};

// 逐項意見與歷程的標籤：固定欄位＋題庫的逐題名字。
const changeLabels = () => ({ ...QC35A_CHANGE_LABELS, ...qc35AnswerLabels() });
const WorkflowQc35aDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/qc35a/${id}`),
        fetchJSON(`/api/workflow/qc35a/${id}/events`),
      ]);
      setData(detail);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (path, body) => {
    setBusy(true);
    setError(null);
    try {
      await fetchJSON(`/api/workflow/qc35a/${id}/${path}`, {
        method: 'POST',
        body: JSON.stringify(body || {}),
      });
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const request = data?.request;
  const { currentEngagement, engagementAttachments, record, review, canReview, canModify, canDelete } = data || {};
  const files = request?.files || [];
  // The engagement's current QC35 documents (for context).
  const currentDocs = request ? (engagementAttachments || []).filter((a) =>
    (a.source === 'qc35') || (a.source === 'own' && a.docType === 'QC35')
  ) : [];

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  const canComment = !!request && (review?.canRejectOnly && request.status === 'requested');
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: changeLabels(),
    expandChanges: expandQc35AnswerChanges,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/qc35a"
      backLabel="QC35 Form Amendment"
      loading={!data}
      error={error}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="QC35 Form Amendment"
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          <StatusActionsCard
            sides={[{
              key: 'main', label: null, status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={busy}
            events={events}
            onApprove={canReview && request.status === 'requested' ? () => act('approve') : null}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify ? () => setModifying(true) : null}
            onResubmit={canModify && request.status === 'awaiting_info' ? () => act('resubmit') : null}
            onDelete={canDelete ? () => setDeleting(true) : null}
          />

          <ChangesSinceReturnCard
            events={events}
            changeLabels={changeLabels()}
            fmtChangeValue={fmtCoverageDefault}
            expandChanges={expandQc35AnswerChanges}
          />

          <ApprovalsCard review={review} approvals={request.approvals || []} status={request.status} />

          <SectionCard title="Amendment">
            <p className="text-sm text-slate-800"><EngagementName name={stripDupFyeLabel(request.engagementLabel)} serviceDescription={request.serviceDescription} /></p>
            {!currentEngagement && <p className="text-xs text-amber-600 mt-1">The engagement no longer exists.</p>}
            {((request.addEngagements || []).length > 0 || (request.removeEngagements || []).length > 0) && (
              <div className="mt-2 text-sm text-slate-700">
                <p className="text-xs text-slate-500 mb-0.5">Coverage Change Requested</p>
                <EngagementNameList
                  items={[
                    ...(request.addEngagements || []).map((e) => ({ ...e, delta: 'add' })),
                    ...(request.removeEngagements || []).map((e) => ({ ...e, delta: 'remove' })),
                  ]}
                  keyOf={(e, i) => `${e.delta}-${e.engagementId || i}`}
                  badgeOf={(e) => (e.delta === 'add' ? <AddedBadge /> : <RemovedBadge />)}
                />
              </div>
            )}
            <div className="mt-3">
              <DetailGrid>
                <DetailItem label="Document">QC35 Form</DetailItem>
                {/* 有帶更正歸檔日就是「改內容」——全部涵蓋案的 Partner 都要簽。 */}
                <DetailItem label="Corrected Archival Date" field="archivalDate">
                  {request.archivalDate ? formatDate(request.archivalDate) : 'Unchanged'}
                </DetailItem>
                <DetailItem label="File Archival Checklist">
                  {request.answers ? 'Corrected — see below' : 'Unchanged'}
                </DetailItem>
                <DetailItem label="Sign-Off Impact">None — the QC35 Form never touches the sign-off date</DetailItem>
                <DetailItem label="Reviewer" field="reviewerName"><NowName value={request.reviewerName} now={request.reviewerNameNow} /></DetailItem>
              </DetailGrid>
            </div>
            <p className="text-xs text-slate-500 mt-3 break-words"><span className="font-medium text-slate-600">Reason:</span> {request.reason}</p>
            <FieldCommentAnchor field="reason" label="Reason for the Amendment" />
          </SectionCard>

          {/* 更正後的 checklist——審核者要先看得到他要核准的那份表。
              沒帶答案的單就整張卡不出現（這張單不改 checklist）。 */}
          {request.answers && (
            <SectionCard title="Corrected File Archival Checklist">
              {/* previousAnswers＝記錄現值：改了的題目帶刪除線前值，審核者
                  一眼看得出哪幾題動了。 */}
              <Qc35ChecklistView
                answers={request.answers}
                previousAnswers={record?.answers || null}
                engagements={currentEngagement ? [currentEngagement] : []}
                extra={(item) => (
                  <FieldCommentAnchor field={`answers.${item.id}`} label={item.label} className="mt-0.5" compact hoverOnly />
                )}
              />
            </SectionCard>
          )}

          <SectionCard title="Corrected Attachments">
            <AttachmentsList
              files={files}
              downloadHref={(f) => `${API_BASE_URL}/api/workflow/qc35a/${request.id}/files/${f.id}`}
              badgeFor={(f) => (f.docType === 'screenshot'
                ? ['Corrected screenshot', 'bg-purple-50 text-purple-700']
                : ['Corrected QC35 Form', 'bg-blue-50 text-blue-700'])}
              emptyText="No attachment — this amendment corrects the checklist and/or the archival date."
            />
            <FieldCommentAnchor field="files" label="Attachments" />
            {currentDocs.length > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-100">
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Engagement's Current QC35 Form</h3>
                <ul className="divide-y divide-slate-100">
                  {currentDocs.map((a) => (
                    <li key={`${a.source}-${a.fileId}`} className="flex items-center gap-2 py-1.5 text-sm">
                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="truncate text-slate-600">{a.originalName}</span>
                      {a.superseded && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500 shrink-0">previous</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </SectionCard>

          <HistoryCard
            events={events}
            actionLabels={{ applied: ['Applied to engagement', 'bg-green-50 text-green-700'] }}
            changeLabels={changeLabels()}
            fmtChangeValue={fmtCoverageDefault}
            expandChanges={expandQc35AnswerChanges}
          />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => { await act('reject', { reason, fieldComments }); setRejecting(false); fc.clear(); }}
            />
          )}

          {modifying && (
            <ModifyModal
              request={request}
              events={events}
              engagement={currentEngagement}
              record={record}
              onClose={() => setModifying(false)}
              onDone={() => { setModifying(false); load(); }}
            />
          )}

          {deleting && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="Withdraw and delete this amendment request?"
              onClose={() => setDeleting(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/workflow/qc35a/${id}`, { method: 'DELETE' });
                navigate('/workflow/qc35a');
              }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

// Modify — revise the reason, the corrected checklist and/or the corrected
// screenshot.
// （涵蓋編輯已退場——一間公司一筆，2026-09-03；legacy 在途單上既有的涵蓋
//   變更留在單上，核准照套。）
const ModifyModal = ({ request, events = [], engagement = null, record = null, onClose, onDone }) => {
  // 記錄現值＝每個更正欄位的起跳點與「有沒有改」的判準。
  const recordAnswers = record ? record.answers : null;
  const recordArchivalDate = (record && record.archivalDate) || '';
  const [reason, setReason] = useState(request.reason || '');
  const [reviewerStaffId, setReviewerStaffId] = useState(
    request.reviewerStaffId ? String(request.reviewerStaffId) : ''
  );
  // Partner only — a reviewer who is not a serving Partner is cleared and must be re-picked.
  useClearOffRosterPick(reviewerStaffId, setReviewerStaffId, 'partner');
  // 起點：這張單已經帶了更正表就接著改，否則從記錄現值開始。
  const [answers, setAnswers] = useState(request.answers || recordAnswers || {});
  // QC6/QC7 現況（評級＋EQR）——checklist 元件抓回來，逐題必填要看它。
  const [qcContext, setQcContext] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  // 更正歸檔日：這張單已帶更正日就接著改，否則從記錄現值起跳。
  const [archivalDate, setArchivalDate] = useState(request.archivalDate || (record && record.archivalDate) || '');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const existingShot = (request.files || []).find((f) => f.docType === 'screenshot');
  // 改回記錄現值＝這張單不再更正那一項（後端同一把尺再判一次）。
  const checklistChanged = !sameQc35Answers(answers, recordAnswers);
  const dateChanged = !!archivalDate && archivalDate !== recordArchivalDate;
  // 匯入記錄（checklist 空白）：整份 checklist 必填——同 New 那一面。
  const legacyRecord = !!(record && record.legacyImport);

  // 動過 checklist（或記錄本來就沒有）就得交出一份完整的表——同 New 那一面。
  const gaps = useMemo(
    () => [
      ...((checklistChanged || legacyRecord) ? qc35AnswerGaps(answers, qcContext) : []),
      ...(reviewerStaffId ? [] : [{ key: 'reviewerName', label: 'Reviewer' }]),
    ],
    [checklistChanged, legacyRecord, answers, qcContext, reviewerStaffId]
  );
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }
    setError(null);
    setSaving(true);
    try {
      const body = new FormData();
      if (reason.trim()) body.set('reason', reason.trim());
      if (reviewerStaffId) body.set('reviewerStaffId', reviewerStaffId);
      body.set('archivalDate', dateChanged ? archivalDate : '');
      body.set('answers', (checklistChanged || legacyRecord) ? JSON.stringify(answers) : '');
      if (screenshot) body.set('screenshot', screenshot);
      await fetchJSON(`/api/workflow/qc35a/${request.id}`, { method: 'PATCH', body });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <FormValidationProvider value={validation.providerValue}>
      <ReturnCommentsBanner events={events} />
      {(error || validation.error) && (
        <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <span>{error || validation.error}</span>
          <button type="button" onClick={() => setError(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      <p className="text-sm text-slate-600"><EngagementName name={stripDupFyeLabel(request.engagementLabel)} serviceDescription={request.serviceDescription} /> — QC35 Form</p>
      {legacyRecord && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
          This record was imported from a signed form — its File archival checklist is empty. Complete the whole
          checklist here.
        </p>
      )}
      {/* checklist 三面同步的第二面——起點是這張單的更正表或記錄現值。 */}
      <Qc35ChecklistForm
        answers={answers}
        onChange={setAnswers}
        engagements={engagement ? [engagement] : []}
        onContext={setQcContext}
      />
      <p className="text-xs text-slate-500">
        {legacyRecord
          ? 'The whole checklist is required for an imported record.'
          : checklistChanged
            ? 'The checklist above will be corrected on approval.'
            : 'Edit any answer above to correct the checklist, or leave it as it is.'}
      </p>
      {/* 現行截圖：檔案輸入框沒辦法預填，記錄上現在掛哪一張要看得到。 */}
      {record?.screenshot && (
        <p className="text-xs text-slate-500">
          Current CaseWare screenshot on file:{' '}
          <a
            href={`${API_BASE_URL}/api/my/engagements/${request.engagementId}/attachments/qc35/${record.screenshot.fileId}`}
            className="text-blue-600 hover:text-blue-800 underline"
            target="_blank"
            rel="noreferrer"
          >
            {record.screenshot.originalName}
          </a>
        </p>
      )}
      <Field label="Replace Corrected CaseWare Screenshot" field="screenshot" hint={existingShot ? `Uploading a new image replaces "${existingShot.originalName}".` : 'PNG / JPG — replaces the record’s current CaseWare screenshot on approval.'}>
        <FilePicker
          accept=".png,.jpg,.jpeg,.gif,.webp"
          onChange={(e) => setScreenshot(e.target.files?.[0] || null)}
          files={screenshot}
        />
        {!screenshot && existingShot && <p className="text-xs text-slate-500 mt-1">Currently attached: {existingShot.originalName}</p>}
      </Field>
      <Field
        label="Corrected Archival Date"
        field="archivalDate"
        hint={dateChanged
          ? 'The archival date will be corrected on approval. Changing it changes every covered engagement.'
          : "Pre-filled with the record's current archival date — change it to correct it."}
      >
        <input type="date" value={archivalDate} onChange={(e) => setArchivalDate(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Reason" field="reason" required>
        <textarea rows="2" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Reviewer" field="reviewerName" required
        hint="You pick who reviews this amendment, from the firm's Partners. Changing it voids recorded approvals.">
        <ReviewerSelect value={reviewerStaffId} onChange={setReviewerStaffId} grade="partner" />
      </Field>
      </FormValidationProvider>
    </FormModal>
  );
};

export default WorkflowQc35aDetail;
